import React, { useContext } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { ResourcedEmoji } from '@atlaskit/emoji/element';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import AudioIcon from '@atlaskit/icon/core/audio';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';

import { EmojiProviderContext } from '../../context/EmojiProviderContext';

const sanitizeEmojiValue = (emojiValue: string | null | undefined) =>
	(emojiValue || '').replace(/['"]+/g, '');

interface PlaylistItemIconProps {
	emojiId?: string; //i.e. "1f404" or "\"\"1f404\"\""
	contentType?: string; //i.e. "page"
	isLive: boolean; //i.e "live"
}

const messages = defineMessages({
	pageIcon: {
		id: 'playlist.item.icon.page',
		defaultMessage: 'Page Icon',
		description: 'accessibility title for Icon that represents a "page" content type',
	},
	liveDocIcon: {
		id: 'playlist.item.icon.liveDoc',
		defaultMessage: 'LiveDoc Icon',
		description: 'accessibility title for Icon that represents a "live page" content type',
	},
	blogPostIcon: {
		id: 'playlist.item.icon.blogPost',
		defaultMessage: 'Blog Post Icon',
		description: 'accessibility title for Icon that represents a "blog post" content type',
	},
});

export const PlaylistItemIcon = ({ emojiId, contentType, isLive }: PlaylistItemIconProps) => {
	const { formatMessage } = useIntl();
	const { emojiProvider } = useContext(EmojiProviderContext);

	const sanitizedEmojiId = sanitizeEmojiValue(emojiId);
	const isPage = contentType === 'page'; //will it always be page? will we support whiteboard or database?
	const isBlogPost = contentType === 'blogpost';
	const hasValidEmoji = sanitizedEmojiId !== 'null' && sanitizedEmojiId !== '';

	if (hasValidEmoji && emojiProvider) {
		return (
			<ResourcedEmoji
				// @ts-ignore shortName is not required
				emojiId={{ id: sanitizedEmojiId }}
				// @ts-ignore doesn't need to be a promise
				emojiProvider={emojiProvider}
				fitToHeight={20}
				showTooltip={false}
			/>
		);
	}

	if (isPage && isLive) {
		return <PageLiveDocIcon size="medium" label={formatMessage(messages.liveDocIcon)} />;
	}

	if (isPage) {
		return <PageIcon size="medium" label={formatMessage(messages.pageIcon)} />;
	}

	if (isBlogPost) {
		return <QuotationMarkIcon size="medium" label={formatMessage(messages.blogPostIcon)} />;
	}

	//todo: remove this if content type will always be page
	return <AudioIcon size="medium" label="Contnet icon" />;
};
